import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Sheet,
  Card,
  Chip,
  Tooltip,
  CircularProgress,
} from '@mui/joy';
import CloseIcon from '@mui/icons-material/Close';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import NumbersIcon from '@mui/icons-material/Numbers';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import TableChartIcon from '@mui/icons-material/TableChart';
import SettingsIcon from '@mui/icons-material/Settings';
import DescriptionIcon from '@mui/icons-material/Description';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import TextSelectionPopover from '../../../components/TextSelectionPopover';

const TYPE_CONFIG = {
  string: { color: 'primary', icon: <TextFieldsIcon sx={{ fontSize: 14 }} />, label: 'Text' },
  number: { color: 'success', icon: <NumbersIcon sx={{ fontSize: 14 }} />, label: 'Number' },
  date: { color: 'warning', icon: <CalendarTodayIcon sx={{ fontSize: 14 }} />, label: 'Date' },
  boolean: { color: 'neutral', icon: <ToggleOnIcon sx={{ fontSize: 14 }} />, label: 'Yes/No' },
  json: { color: 'info', icon: <TableChartIcon sx={{ fontSize: 14 }} />, label: 'Table' },
};

const getTypeConfig = (type) => TYPE_CONFIG[type] || TYPE_CONFIG.string;

const FieldCard = ({ field, onQuote, focused }) => {
  const typeConfig = getTypeConfig(field.type);
  const cardRef = useRef(null);

  useEffect(() => {
    if (focused && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focused]);

  const handleQuoteClick = () => {
    const quoteText = `Field: ${field.key} (${field.type})${field.description ? ` - "${field.description}"` : ''}`;
    onQuote(quoteText);
  };

  return (
    <Card
      ref={cardRef}
      variant="outlined"
      sx={{
        p: 2,
        mb: 1.5,
        ...(focused && {
          borderColor: 'primary.400',
          borderWidth: 2,
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? 'primary.900' : 'primary.50',
        }),
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography level="title-sm" sx={{ fontWeight: 600 }}>
            {field.key}
          </Typography>
          <Chip size="sm" variant="soft" color={typeConfig.color} startDecorator={typeConfig.icon}>
            {typeConfig.label}
          </Chip>
          {field.page && (
            <Chip size="sm" variant="outlined" color="neutral">
              Page {field.page}
            </Chip>
          )}
        </Stack>
        <Tooltip title="Quote this field" size="sm">
          <IconButton size="sm" variant="plain" color="neutral" onClick={handleQuoteClick}>
            <FormatQuoteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Stack>
      {field.description && (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1.5 }}>
          <DescriptionIcon sx={{ fontSize: 16, color: 'text.tertiary', mt: 0.25 }} />
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            {field.description}
          </Typography>
        </Stack>
      )}
      {field.meaning && (
        <Typography level="body-xs" sx={{ mt: 1, color: 'text.tertiary', fontStyle: 'italic' }}>
          {field.meaning}
        </Typography>
      )}
      {field.usual_location && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
          <LocationOnIcon sx={{ fontSize: 14, color: 'text.tertiary' }} />
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
            {field.usual_location}
          </Typography>
        </Stack>
      )}
      {field.type === 'json' && field.structure?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography level="body-xs" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
            Table Columns:
          </Typography>
          <Stack spacing={0.5}>
            {field.structure.map((col, idx) => (
              <Stack
                key={idx}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ pl: 1.5, py: 0.5, borderLeft: '2px solid', borderColor: 'primary.300' }}
              >
                <Typography level="body-xs" sx={{ fontWeight: 500, minWidth: 80 }}>
                  {col.key}
                </Typography>
                <Chip size="sm" variant="soft" color="neutral" sx={{ fontSize: '10px', height: 18 }}>
                  {col.type}
                </Chip>
                {col.value && (
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    {col.value}
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Card>
  );
};

const SettingsSection = ({ settings }) => {
  if (!settings || Object.keys(settings).length === 0) return null;

  const displaySettings = [
    { key: 'tool', label: 'Tool', value: settings.tool },
    { key: 'type', label: 'Type', value: settings.type },
    { key: 'minimum_fields_percent', label: 'Min Fields %', value: settings.minimum_fields_percent },
    { key: 'minimum_validation_percent', label: 'Min Validation %', value: settings.minimum_validation_percent },
    { key: 'handwriting_enabled', label: 'Handwriting', value: settings.handwriting_enabled, isBoolean: true },
    { key: 'use_azure_document_inteligence', label: 'Azure Doc Intelligence', value: settings.use_azure_document_inteligence, isBoolean: true },
  ].filter((s) => s.value !== undefined && s.value !== '' && s.value !== null);

  if (displaySettings.length === 0) return null;

  return (
    <Card variant="soft" sx={{ p: 2, mt: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <SettingsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography level="title-sm">Settings</Typography>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 1.5 }}>
        {displaySettings.map(({ key, label, value, isBoolean }) => (
          <Stack key={key} spacing={0.25}>
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              {label}
            </Typography>
            {isBoolean ? (
              <Chip size="sm" variant="soft" color={value ? 'success' : 'neutral'}>
                {value ? 'Enabled' : 'Disabled'}
              </Chip>
            ) : (
              <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                {value}
              </Typography>
            )}
          </Stack>
        ))}
      </Box>
    </Card>
  );
};

const PolicyViewerDrawer = ({ open, onClose, policy = null, loading = false, onQuote = () => {}, noWrapper = false, focusFieldKey }) => {
  const [viewTab, setViewTab] = useState(0);

  useEffect(() => {
    setViewTab(0);
  }, [policy?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const policyData = policy?.policy || {};
  const fields = policyData.documentFieldsSchema || [];
  const settings = policy?.settings;

  const innerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
      >
        <Typography level="title-md" noWrap sx={{ fontWeight: 600, flex: 1, mr: 1 }}>
          {policy?.name || 'Policy'}
        </Typography>
        <IconButton size="sm" variant="plain" color="neutral" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {loading && (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress size="md" />
            <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
              Loading policy…
            </Typography>
          </Stack>
        )}

        {!loading && !policy && (
          <Typography level="body-sm" color="neutral" sx={{ textAlign: 'center', py: 4 }}>
            No policy selected
          </Typography>
        )}

        {!loading && policy && (
          <Tabs value={viewTab} onChange={(_, value) => setViewTab(value)} sx={{ bgcolor: 'transparent' }}>
            <TabList size="sm" sx={{ mb: 1.5 }}>
              <Tab value={0}>Pretty</Tab>
              <Tab value={1}>Raw JSON</Tab>
            </TabList>
            <TabPanel value={0} sx={{ p: 0 }}>
              <TextSelectionPopover source="policy" onQuote={onQuote}>
                <Box>
                  {/* Description */}
                  {policyData.description && (
                    <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
                      {policyData.description}
                    </Typography>
                  )}

                  {/* Fields */}
                  {fields.length > 0 ? (
                    <Box>
                      <Typography level="title-sm" sx={{ mb: 1.5, color: 'text.secondary' }}>
                        Document Fields ({fields.length})
                      </Typography>
                      {fields.map((field, index) => (
                        <FieldCard key={field.key || index} field={field} onQuote={onQuote} focused={focusFieldKey === field.key} />
                      ))}
                    </Box>
                  ) : (
                    <Typography level="body-sm" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                      No fields defined
                    </Typography>
                  )}

                  <SettingsSection settings={settings} />

                  {/* Metadata */}
                  {(policy?.created_at || policy?.last_modified) && (
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
                    >
                      {policy.created_at && (
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                          Created: {new Date(policy.created_at).toLocaleDateString()}
                        </Typography>
                      )}
                      {policy.last_modified && (
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                          Modified: {new Date(policy.last_modified).toLocaleDateString()}
                        </Typography>
                      )}
                    </Stack>
                  )}
                </Box>
              </TextSelectionPopover>
            </TabPanel>
            <TabPanel value={1} sx={{ p: 0 }}>
              <TextSelectionPopover source="policy" onQuote={onQuote}>
                <Sheet variant="outlined" sx={{ overflow: 'auto', borderRadius: 'sm' }}>
                  <pre style={{ margin: 0, padding: '12px', fontSize: '12px' }}>
                    {JSON.stringify(policy, null, 2)}
                  </pre>
                </Sheet>
              </TextSelectionPopover>
            </TabPanel>
          </Tabs>
        )}
      </Box>
    </Box>
  );

  if (noWrapper) {
    return innerContent;
  }

  return (
    <Box
      aria-hidden={!open}
      sx={{
        flexBasis: open ? { xs: '100%', md: '40vw' } : '0px',
        width: open ? { xs: '100%', md: '40vw' } : '0px',
        minWidth: 0,
        maxWidth: open ? { xs: '100%', md: '700px' } : '0px',
        opacity: open ? 1 : 0,
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        pointerEvents: open ? 'auto' : 'none',
        borderLeftStyle: 'solid',
        borderLeftWidth: open ? '1px' : '0px',
        borderColor: 'divider',
        bgcolor: 'background.surface',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        flexShrink: 0,
        overflow: 'hidden',
        willChange: 'flex-basis, width, max-width, transform, opacity',
        transition:
          'flex-basis 380ms cubic-bezier(0.4, 0, 0.2, 1), width 380ms cubic-bezier(0.4, 0, 0.2, 1), max-width 380ms cubic-bezier(0.4, 0, 0.2, 1), opacity 220ms ease, transform 380ms cubic-bezier(0.4, 0, 0.2, 1), border-left-width 380ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {innerContent}
    </Box>
  );
};

export default PolicyViewerDrawer;
